import Button from 'components/shared/Button';
import React, { useState } from 'react';
import styles from './CouponInfo.module.scss';
import { SubscriptionStatus } from 'features/billing/subscription/subscriptionTypes';
import { useSelector } from 'react-redux';
import { couponInfoSelector, isLoadingCouponSelector } from 'features/billing/couponInfo/couponInfoSlice';
import { toast } from 'react-toastify';
import { clearCouponInfo, getCouponByPromotionCode } from 'features/billing/couponInfo/couponInfoActions';
import { useAppDispatch } from 'app/store';
import { currenciesListSelector } from 'features/currencies/currenciesSlice';
import { subscriptionInfoSelector } from 'features/billing/subscription/subscriptionSlice';
import { setSubscriptionFormInfoCouponId } from 'features/billing/formStates/billingFormsInfo/billingFormsInfoActions';
import { updateSubscriptionInfo } from 'features/billing/subscription/subscriptionActions';
import { billingFormsInfoSelector } from 'features/billing/formStates/billingFormsInfo/billingFormsInfoSlice';
import CustomInput from 'components/shared/forms/CustomInput';

const IntervalSelectionButtons: React.FC = () => {
  const dispatch = useAppDispatch();

  const subscriptionInfo = useSelector(subscriptionInfoSelector);
  const currencies = useSelector(currenciesListSelector)
  const couponInfo = useSelector(couponInfoSelector);
  const couponIsLoading = useSelector(isLoadingCouponSelector);

  const billingFormsInfo = useSelector(billingFormsInfoSelector);
  const { subscriptionFormInfo } = billingFormsInfo;

  const getCurrencySymbol = (currencyCode: string) => currencies.find(x => x.code.toLowerCase() === currencyCode.toLowerCase())?.symbol ?? currencyCode
  const currentCurrencySymbol = getCurrencySymbol(subscriptionFormInfo?.currency ?? subscriptionInfo!.currency)

  const subscriptionIsActive = subscriptionInfo!.status === SubscriptionStatus.Active;
  const subscriptionIsTrialing = subscriptionInfo!.status === SubscriptionStatus.Trialing;
  const isActiveOrTrialing = subscriptionIsActive || subscriptionIsTrialing;

  const [promotionCode, setPromotionCode] = useState('');
  const [promotionCodeInputIsShown, setPromotionCodeInputIsShown] = useState(false);

  const handleApplyPromotionCode = (promoCode: string) => {
    promoCode = promoCode.trim()
    if (!promoCode) {
      toast.error('Promo code field must not be blank.')
      return;
    }

    dispatch(getCouponByPromotionCode(promoCode))
      .then(action => {
        if (getCouponByPromotionCode.fulfilled.match(action)) {
          const coupon = action.payload;

          if (!coupon.canBeUsed) {
            toast.error("This promo code is invalid or no longer active. It cannot be applied.")
            dispatch(clearCouponInfo());
            return;
          }

          toast.success("Coupon applied successfully!")

          if (isActiveOrTrialing) {
            dispatch(updateSubscriptionInfo({
              subscriptionId: subscriptionInfo!.id,
              priceId: subscriptionInfo!.priceId,
              quantity: subscriptionInfo!.quantity,
              couponId: coupon.id
            }));
          }

          dispatch(setSubscriptionFormInfoCouponId(coupon.id));
        }
      });
  }

  const handleCancelPromotionCode = () => {
    if (isActiveOrTrialing) {
      dispatch(updateSubscriptionInfo({
        subscriptionId: subscriptionInfo!.id,
        priceId: subscriptionInfo!.priceId,
        quantity: subscriptionInfo!.quantity,
        couponId: null
      }));
    }

    dispatch(clearCouponInfo())
    dispatch(setSubscriptionFormInfoCouponId(null))
    setPromotionCode('')
    setPromotionCodeInputIsShown(false)
  }

  if (couponInfo) {
    return (
      <div className={`${styles.container} ${styles.spaceBetween} ${styles.alignItemsCenter} ${styles.bgLightGreen}`}>
        <p>
          {couponInfo.amountOff && `${currentCurrencySymbol}${couponInfo.amountOff} `}
          {couponInfo.percentOff && `${couponInfo.percentOff}% `}
          discount applied
          {(couponInfo.percentOff ?? 0) === 100 && " - enjoy free access!"}
        </p>
        <button className={`${styles.cancelPromotionCodeButton}`} onClick={handleCancelPromotionCode}>
          <svg width="11" height="11" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7.0001 8.0501L1.7501 13.3001C1.6001 13.4501 1.4251 13.5251 1.2251 13.5251C1.0251 13.5251 0.850098 13.4501 0.700098 13.3001C0.550098 13.1501 0.475098 12.9751 0.475098 12.7751C0.475098 12.5751 0.550098 12.4001 0.700098 12.2501L5.9501 7.0001L0.700098 1.7501C0.550098 1.6001 0.475098 1.4251 0.475098 1.2251C0.475098 1.0251 0.550098 0.850098 0.700098 0.700098C0.850098 0.550098 1.0251 0.475098 1.2251 0.475098C1.4251 0.475098 1.6001 0.550098 1.7501 0.700098L7.0001 5.9501L12.2501 0.700098C12.4001 0.550098 12.5751 0.475098 12.7751 0.475098C12.9751 0.475098 13.1501 0.550098 13.3001 0.700098C13.4501 0.850098 13.5251 1.0251 13.5251 1.2251C13.5251 1.4251 13.4501 1.6001 13.3001 1.7501L8.0501 7.0001L13.3001 12.2501C13.4501 12.4001 13.5251 12.5751 13.5251 12.7751C13.5251 12.9751 13.4501 13.1501 13.3001 13.3001C13.1501 13.4501 12.9751 13.5251 12.7751 13.5251C12.5751 13.5251 12.4001 13.4501 12.2501 13.3001L7.0001 8.0501Z"></path></svg>
        </button>
      </div>
    )
  }
  else {
    return (
      <>
        <p
          onClick={() => setPromotionCodeInputIsShown(prev => !prev)}
          className={`${styles.justifyContentFlexEnd} ${styles.havePromotionCodeLink}`}
        >
          Have a promotion code?
        </p>

        {promotionCodeInputIsShown && (
          <div className={`${styles.container} ${styles.spaceBetween} ${styles.alignItemsLastBaseline} ${styles.bgLightBlue}`}>
            <>
              <div className={styles.widthfull}>
                <CustomInput
                  type="text"
                  label="Promotion code"
                  value={promotionCode}
                  onChange={e => setPromotionCode(e.target.value)}
                />
              </div>
              <Button
                style={{ width: 100 }}
                preloader={couponIsLoading}
                variant="primary"
                onClick={() => handleApplyPromotionCode(promotionCode)}
              >
                Apply
              </Button>
            </>
          </div>
        )}
      </>
    )
  }
};

export default IntervalSelectionButtons;
